import { billKindSchema, gstModeSchema } from "@luckymenu/core";
import { z } from "zod";
import type { BillCalculation } from "./bill";
import { BILL_SEQ_DIGITS, billNoSchema, financialYearSchema } from "./bill-number";
import type { DiscountInput } from "./discount";
import { billKindFor } from "./gst";

/**
 * `bills.snapshot`: everything needed to show or reprint a bill without
 * reading any other table (data-model.md, "Billing"). Written once, when the
 * bill is finalised, and never changed.
 *
 * `version` lets a later release read snapshots written by an earlier one.
 */

const paise = z.number().int().nonnegative();
const signedPaise = z.number().int();
const basisPoints = z.number().int().min(0).max(10000);
const text = (max: number) => z.string().trim().max(max);

export const discountRuleSchema = z.discriminatedUnion("type", [
	z.object({ type: z.literal("percent"), bp: basisPoints, maxPaise: paise.nullable() }),
	z.object({ type: z.literal("amount"), paise }),
]);

/** A discount as staff enter it or an offer produces it. Also the API input shape. */
export const discountInputSchema = z.object({
	rule: discountRuleSchema,
	label: text(60).nullable(),
	reason: text(200).nullable(),
	offerId: z.uuid().nullable(),
}) satisfies z.ZodType<DiscountInput>;

const billLineSchema = z.object({
	id: z.string(),
	name: z.string(),
	variantName: z.string().nullable(),
	modifiers: z.array(z.object({ name: z.string(), priceDeltaPaise: signedPaise })),
	unitPricePaise: paise,
	modifiersTotalPaise: signedPaise,
	quantity: z.number().int().positive(),
	taxRateBp: basisPoints,
	grossPaise: paise,
	discount: discountInputSchema.nullable(),
	itemDiscountPaise: paise,
	billDiscountPaise: paise,
	netPaise: paise,
});

export const billCalculationSchema = z.object({
	gstMode: gstModeSchema,
	pricesIncludeTax: z.boolean(),
	roundOff: z.boolean(),
	lines: z.array(billLineSchema),
	billDiscount: discountInputSchema.extend({ amountPaise: paise }).nullable(),
	serviceCharge: z
		.object({ rateBp: basisPoints, amountPaise: paise, taxRateBp: basisPoints })
		.nullable(),
	taxes: z.array(
		z.object({ rateBp: basisPoints, taxablePaise: paise, cgstPaise: paise, sgstPaise: paise }),
	),
	totals: z.object({
		subtotalPaise: paise,
		itemDiscountPaise: paise,
		billDiscountPaise: paise,
		discountPaise: paise,
		serviceChargePaise: paise,
		taxablePaise: paise,
		cgstPaise: paise,
		sgstPaise: paise,
		roundOffPaise: z.number().int().min(-50).max(50),
		totalPaise: paise,
	}),
	/** Bills from before Phase 9 have none recorded. */
	offers: z
		.array(
			z.object({
				offerId: z.uuid(),
				label: z.string(),
				scope: z.enum(["item", "bill"]),
				discountPaise: paise,
			}),
		)
		.default([]),
}) satisfies z.ZodType<BillCalculation, unknown>;

export const billSnapshotSchema = z
	.object({
		version: z.literal(1),
		kind: billKindSchema,
		billNo: billNoSchema,
		series: z.string().min(1).max(4),
		fy: financialYearSchema,
		seq: z.number().int().positive(),
		/** YYYY-MM-DD in the restaurant's time zone, after the day start hour. */
		businessDate: z.iso.date(),
		finalizedAt: z.iso.datetime(),
		timeZone: z.string(),
		seller: z.object({
			name: z.string(),
			legalName: z.string().nullable(),
			addressLine1: z.string().nullable(),
			addressLine2: z.string().nullable(),
			city: z.string().nullable(),
			stateCode: z.string().nullable(),
			pincode: z.string().nullable(),
			phone: z.string().nullable(),
			/** Required on a tax invoice and a bill of supply. */
			gstin: z.string().nullable(),
			fssaiNo: z.string().nullable(),
			/** 996331 on a tax invoice, otherwise null. */
			sac: z.string().nullable(),
			header: z.string().nullable(),
			footer: z.string().nullable(),
			/** For the "scan to pay" QR on reprints. */
			upi: z.object({ vpa: z.string(), payeeName: z.string() }).nullable(),
		}),
		customer: z.object({
			name: z.string().nullable(),
			phone: z.string().nullable(),
			gstin: z.string().nullable(),
		}),
		order: z.object({
			/** The daily order number; the token number of a takeaway. */
			orderNo: z.number().int().positive(),
			/** Null for a takeaway. */
			tableLabel: z.string().nullable(),
		}),
		calculation: billCalculationSchema,
	})
	.superRefine((s, ctx) => {
		if (s.kind !== billKindFor(s.calculation.gstMode)) {
			ctx.addIssue({
				code: "custom",
				path: ["kind"],
				message: `A ${s.calculation.gstMode} bill is a ${billKindFor(s.calculation.gstMode)}`,
			});
		}
		if (s.kind !== "plain" && !s.seller.gstin) {
			ctx.addIssue({ code: "custom", path: ["seller", "gstin"], message: "GSTIN is required" });
		}
		if ((s.kind === "tax_invoice") !== (s.seller.sac !== null)) {
			ctx.addIssue({
				code: "custom",
				path: ["seller", "sac"],
				message: "SAC is printed on a tax invoice only",
			});
		}
		if (!s.billNo.endsWith(`${s.fy}-${String(s.seq).padStart(BILL_SEQ_DIGITS, "0")}`)) {
			ctx.addIssue({
				code: "custom",
				path: ["billNo"],
				message: "The bill number must end with the financial year and sequence",
			});
		}
	});

export type BillSnapshot = z.infer<typeof billSnapshotSchema>;
